import { useEffect, useState } from 'react'
import * as microsoftTeams from '@microsoft/teams-js'
import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig } from '@config/auth.config'

/**
 * Page de fin d'authentification pour Teams
 * Cette page reçoit le callback d'Azure AD et transmet le résultat à Teams
 */
const AuthEnd = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const handleAuthEnd = async () => {
      try {
        // Initialiser Teams SDK
        await microsoftTeams.app.initialize()

        // Initialiser MSAL et gérer le callback
        const msalInstance = new PublicClientApplication(msalConfig)
        await msalInstance.initialize()

        // Traiter la réponse de redirection
        const response = await msalInstance.handleRedirectPromise()

        if (response && response.accessToken) {
          // Authentification réussie - notifier la fenêtre parente
          setStatus('success')
          microsoftTeams.authentication.notifySuccess(response.accessToken)
        } else {
          // Vérifier s'il y a une erreur dans l'URL
          const hash = window.location.hash
          if (hash.includes('error')) {
            throw new Error('Authentication failed')
          }
          
          // Pas de token mais pas d'erreur - peut-être une redirection intermédiaire
          setStatus('error')
          setErrorMessage('No access token received')
          microsoftTeams.authentication.notifyFailure('No access token received')
        }
      } catch (error) {
        console.error('Auth end error:', error)
        setStatus('error')
        const message = error instanceof Error ? error.message : 'Authentication failed'
        setErrorMessage(message)
        
        try {
          await microsoftTeams.app.initialize()
          microsoftTeams.authentication.notifyFailure(message)
        } catch {
          // Si Teams SDK n'est pas disponible, fermer simplement la fenêtre
          window.close()
        }
      }
    }

    handleAuthEnd()
  }, [])

  return (
    <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
      <div className="text-center p-6">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Finalisation de l'authentification...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <p className="text-gray-600 dark:text-gray-300">Authentification réussie</p>
            <p className="text-sm text-gray-500 mt-2">Cette fenêtre va se fermer automatiquement...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="text-red-500 text-5xl mb-4">✗</div>
            <p className="text-gray-600 dark:text-gray-300">Erreur d'authentification</p>
            <p className="text-sm text-red-500 mt-2">{errorMessage}</p>
          </>
        )}
      </div>
    </div>
  )
}

export default AuthEnd
